// =====================================================================================
// INVRT Radar — web console — one site — Known customers tab
//
// Two halves. The LIST (C1, docs/API-CONTRACTS-C1.md §4: GET /sites/:id/known-customers
// → {entries:[{id, kind, value, created_at}], next_cursor}) shows every person and domain
// Radar will never bill you to identify, paged through the opaque cursor; any member may
// read it. The UPLOAD (the original body of this file, moved verbatim out of
// SiteDetailPage.tsx in the C1 split) is organisation-admin only, exactly as the server
// gates POST /sites/:id/known-customers.
//
// The list is the organisation's, not one site's (the server stores known customers
// org-wide and resolves :id only to prove ownership), and the copy says so.
// =====================================================================================

import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDate, humaniseAction, pluralise } from "@/lib/format";
import type { KnownCustomerEntry, Site } from "@/lib/types";
import type { KnownCustomerList } from "@/lib/types.c1";

const PAGE_SIZE = 50;

function parseKnownCustomerCsv(raw: string): KnownCustomerEntry[] {
  const entries: KnownCustomerEntry[] = [];
  for (const line of raw.split(/\r?\n/)) {
    for (const cell of line.split(",")) {
      const value = cell.trim().replace(/^"|"$/g, "");
      if (value === "") continue;
      // A header row names its columns; skip those rather than suppressing the word "email".
      if (/^(email|domain|company|e-mail)$/i.test(value)) continue;
      if (value.includes("@")) entries.push({ email: value.toLowerCase() });
      else if (value.includes(".")) entries.push({ domain: value.toLowerCase() });
    }
  }
  return entries;
}

/**
 * The wire carries `email` and `domain` today (the server's list filters to those two
 * kinds); anything else a newer server might add is humanised rather than hidden.
 */
function kindLabel(kind: string): string {
  if (kind === "email") return "Email";
  if (kind === "domain") return "Domain";
  if (kind === "company") return "Company";
  return humaniseAction(kind);
}

export function KnownCustomersPanel({ site }: { site: Site }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId, activeMembership } = useSession();
  const { push } = useToast();
  const [raw, setRaw] = useState("");
  const [reset, setReset] = useState(false);

  const listKey = ["known-customers", orgId, site.id];
  const list = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) =>
      api.get<KnownCustomerList>(`/sites/${site.id}/known-customers`, {
        query: { limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Boolean(orgId),
  });

  const parsed = useMemo(() => parseKnownCustomerCsv(raw), [raw]);

  const upload = useMutation({
    mutationFn: () =>
      api.post<{ submitted?: number }>(`/sites/${site.id}/known-customers`, {
        entries: parsed,
        reset,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      push({
        tone: "success",
        title: "Known customers updated",
        body: "Radar will not bill you for a visitor on this list.",
      });
      setRaw("");
    },
  });

  const isAdmin = activeMembership?.is_org_admin === true;
  const entries = list.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Customers already on your list</CardTitle>
          <p className="text-sm text-ink-muted">
            Identified visitors are matched against these email addresses and domains, and
            Radar never charges you for one of them. The list is shared by every site in your
            organisation.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {list.isLoading ? <LoadingState label="Loading your known customers…" /> : null}
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : null}
          {list.isSuccess ? (
            entries.length === 0 ? (
              <EmptyState
                title="No known customers yet"
                body={
                  isAdmin
                    ? "Upload email addresses and domains below and Radar will never charge you to identify someone already yours."
                    : "An administrator on your team can upload the list."
                }
              />
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Kind</TableHeaderCell>
                      <TableHeaderCell>Value</TableHeaderCell>
                      <TableHeaderCell>Added</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Badge tone={entry.kind === "email" ? "info" : "neutral"}>
                            {kindLabel(entry.kind)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{entry.value}</TableCell>
                        <TableCell className="text-ink-muted">{formatDate(entry.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-ink-muted">
                    {pluralise(entries.length, "entry", "entries")} shown
                    {list.hasNextPage ? ", more available" : ""}.
                  </p>
                  {list.hasNextPage ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={list.isFetchingNextPage}
                      onClick={() => void list.fetchNextPage()}
                    >
                      Load more
                    </Button>
                  ) : null}
                </div>
              </>
            )
          ) : null}
        </CardContent>
      </Card>

      {isAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Add customers you already have</CardTitle>
            <p className="text-sm text-ink-muted">
              Paste a CSV or a plain list of email addresses and domains. Radar matches
              identified visitors against it and never charges you for someone already yours.
              Do this before turning the pixel on, not after.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Textarea
              label="Email addresses and domains"
              value={raw}
              onChange={(event) => setRaw(event.target.value)}
              placeholder={"finance@acme.co.uk\nacme.co.uk\nbigcustomer.com"}
              hint="One per line, or a CSV file's contents pasted straight in. A header row is ignored."
              rows={8}
            />

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={reset}
                onChange={(event) => setReset(event.target.checked)}
              />
              Replace the existing list rather than adding to it
            </label>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => upload.mutate()}
                loading={upload.isPending}
                disabled={parsed.length === 0}
              >
                Upload {parsed.length > 0 ? pluralise(parsed.length, "entry", "entries") : "list"}
              </Button>
              {raw.trim() !== "" && parsed.length === 0 ? (
                <span className="text-sm text-warning">
                  Nothing in that text looks like an email address or a domain.
                </span>
              ) : null}
            </div>

            {upload.isError ? <ErrorState error={upload.error} /> : null}
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-ink-muted">
          Your known-customer list decides who Radar will never charge you to identify. An
          administrator on your team can upload or replace it.
        </p>
      )}
    </div>
  );
}
